import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import { getExerciseWeek } from "../../../../utils/cycle-helpers";
import {
  authoredWeekRange,
  authoredWeeks,
  resolveFlexibleOccurrence,
  resolveSplitType,
  SPLIT_FORCED,
  SPLIT_SUGGESTED,
} from "../../../../utils/flexible-program";
import {
  CardsSkeleton,
  SlotExerciseList,
} from "../../common-components/ProgramSlotCard";
import WeekStartModal from "./WeekStartModal";

const pretty = (yyyymmdd) =>
  yyyymmdd ? moment(String(yyyymmdd), "YYYYMMDD").format("MMM D, YYYY") : "";

/** Month and day only — the repeating chip has to stay on one line. */
const prettyMonthDay = (yyyymmdd) =>
  yyyymmdd ? moment(String(yyyymmdd), "YYYYMMDD").format("MMM D") : "";

/** D1..D7 - a week never offers more than seven workout slots. */
const SLOT_ORDERS = Array.from({ length: 7 }, (_, index) => index + 1);

const prettyShort = (yyyymmdd) =>
  yyyymmdd ? moment(String(yyyymmdd), "YYYYMMDD").format("ddd MMM D") : "";

/**
 * Week-by-week overview of one client's Flexible Program.
 *
 * Rows are the weeks the coach actually AUTHORED - one row each, exactly like
 * the team manager. A repeating week used to render one row per re-issue, which
 * projected a wall of identical future weeks the coach had never written; rows
 * now only appear as weeks are added. The per-occurrence claim data is still
 * loaded (flexible-program-overview, capped at today by the controller) and each
 * week shows the progress of its MOST RECENT occurrence, so a repeating week
 * reports the week the client is actually in rather than a stale first run.
 *
 * Forked from TeamProgramManager rather than sharing it: that component lays
 * slots out across the seven weekdays via
 * `sessions.find(s => s.workoutDay === dayNumber)`, which returns the same
 * session seven times whenever workoutDay isn't set per slot. A flexible
 * program lists slots as D1..Dn ordinals instead.
 */
const FlexibleProgramManager = ({ locals, api, onImport }) => {
  const { t } = useTranslation("coaching-portal");
  const { withWeight, fromMetric, activeUserToRequest } = locals;
  const cycle = locals?.editCycle;
  const athlete = locals?.query?.athlete;
  const occurrences = locals?.flexibleProgramOverview?.occurrences || [];

  const [weekFilter, setWeekFilter] = useState("upcoming");
  const [exerciseWeek, setExerciseWeek] = useState(null);
  const [cycleExercises, setCycleExercises] = useState(null);
  const [weekExercises, setWeekExercises] = useState({});
  const [weekSections, setWeekSections] = useState({});
  const [loading, setLoading] = useState(null);
  // The week being duplicated while its start-date picker is open - null
  // when closed.
  const [duplicating, setDuplicating] = useState(null);
  // Mirrors the header's own "+ Add new week" (FlexibleProgram.js) - a
  // second entry point at the bottom of the list, same as the team manager's
  // own add-new-week row (TeamProgramManager.js), but routed through this
  // program's own start-date picker rather than team's direct, modal-less add.
  const [addingWeek, setAddingWeek] = useState(false);

  // Same derivation as the team manager: the stored preference is "rpe"/"rir",
  // but the set formatter wants the display token.
  const intensityMeasure =
    (activeUserToRequest?.intensityMeasure ?? "rpe") === "rpe" ? "RPE" : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const units = activeUserToRequest?.units;
  const webAppTheme = activeUserToRequest?.webAppTheme || "dark";

  useEffect(() => {
    const requestWeek = async () => {
      setLoading(exerciseWeek);
      const response = await getExerciseWeek(api, cycle._id, exerciseWeek);
      setCycleExercises(response);
    };

    if (exerciseWeek) requestWeek();
  }, [exerciseWeek]);

  useEffect(() => {
    if (!cycleExercises || !exerciseWeek) return;

    const { exercises, sections } = cycleExercises;
    // An authored week is always a single week, so week "1" is the only key.
    setWeekExercises((prev) => ({ ...prev, [exerciseWeek]: exercises?.["1"] }));
    setWeekSections((prev) => ({ ...prev, [exerciseWeek]: sections || [] }));
    setCycleExercises(null);
    setLoading(null);
  }, [cycleExercises, exerciseWeek]);

  const today = Number(moment().format("YYYYMMDD"));
  const weeks = authoredWeeks(cycle);

  // A repeating week has many occurrences; the coach cares about the one the
  // client is in now, so each week keeps only its latest.
  const latestOccurrence = new Map();
  for (const occurrence of occurrences) {
    const order = Number(occurrence.programOrder);
    const held = latestOccurrence.get(order);
    if (!held || occurrence.occurrenceStartDate > held.occurrenceStartDate) {
      latestOccurrence.set(order, occurrence);
    }
  }

  // The week governing RIGHT NOW - the newest one that has started.
  const live = resolveFlexibleOccurrence(cycle, today);
  const liveOrder =
    live?.status === "active" ? Number(live.programOrder) : null;
  const lastWeekOrder = weeks.length
    ? Number(weeks[weeks.length - 1].order)
    : null;

  // A week is history once a newer authored week has started and taken over.
  const isSuperseded = (index) => {
    const next = weeks[index + 1];
    return Boolean(next && Number(next.startDate) <= today);
  };
  const visible = weeks.filter((_week, index) =>
    weekFilter === "history" ? isSuperseded(index) : !isSuperseded(index)
  );

  const liveRange = liveOrder ? authoredWeekRange(cycle, liveOrder) : null;

  const duplicateWeek = (programOrder, startDate) => {
    submit(`/actions/cycle/${cycle._id}/flexible-duplicate-week`, {
      method: "POST",
      body: {
        athlete,
        programOrder,
        today,
        ...(startDate ? { startDate } : {}),
      },
    });
  };

  const addWeek = (startDate) => {
    submit(`/actions/cycle/${cycle._id}/flexible-add-week`, {
      method: "POST",
      body: { athlete, today, ...(startDate ? { startDate } : {}) },
    });
  };

  return (
    <div>
      {/* Keyed on the cycle so switching athlete (same page, same component
          position - see FlexibleProgram.js' own athlete-switch handling)
          remounts this fresh instead of re-rendering in place. Without it,
          `name`/`dirty` below are plain useState, seeded once from `cycle`
          at mount - a later prop change doesn't touch them, so the input
          kept showing the PREVIOUS athlete's program name until the coach
          happened to focus and blur it. */}
      <FlexibleTopPanel
        key={cycle?._id}
        cycle={cycle}
        athlete={athlete}
        liveRange={liveRange}
        t={t}
      />

      <hgroup className="history-filter-buttons">
        <button
          type="button"
          data-button-type={weekFilter === "history" ? "outline" : "secondary"}
          onClick={() => setWeekFilter("history")}
        >
          {t("flexible.manager.history")}
        </button>
        <button
          type="button"
          data-button-type={weekFilter === "upcoming" ? "outline" : "secondary"}
          onClick={() => setWeekFilter("upcoming")}
        >
          {t("flexible.manager.upcoming")}
        </button>
      </hgroup>

      <div className="program-container">
        <div className="flexible-week-header-row grid grid-cols-6 px-6 py-6 items-center">
          <p className="s1">{t("flexible.manager.week")}</p>
          <p className="s1">{t("flexible.manager.name")}</p>
          <p className="s1">{t("flexible.manager.workouts")}</p>
          <p className="s1">{t("flexible.manager.split")}</p>
          <p className="s1">{t("flexible.manager.daysTrained")}</p>
          <p className="s1 text-right">{t("flexible.manager.action")}</p>
        </div>

        <div className="programs">
          {visible.length === 0 ? (
            <section className="no-data-state">
              <p className="s1">{t("flexible.manager.noWeeks")}</p>
            </section>
          ) : (
            visible.map((week) => (
              <WeekRow
                key={`week-${week.order}`}
                week={week}
                occurrence={latestOccurrence.get(Number(week.order)) || null}
                cycle={cycle}
                athlete={athlete}
                isLive={Number(week.order) === liveOrder}
                isLastWeek={Number(week.order) === lastWeekOrder}
                isHistory={weekFilter === "history"}
                weekExercises={weekExercises}
                weekSections={weekSections}
                setExerciseWeek={setExerciseWeek}
                loading={loading}
                withWeight={withWeight}
                fromMetric={fromMetric}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                units={units}
                webAppTheme={webAppTheme}
                // Always confirms a start date first, same as Add new week -
                // the picker lists weeks well beyond just the next boundary,
                // so there's a real choice worth seeing regardless of which
                // week is being duplicated or whether it's live/repeating.
                onDuplicate={() => setDuplicating(week)}
                t={t}
              />
            ))
          )}
        </div>

        {visible.length > 0 && weekFilter !== "history" && (
          <div className="add-new-week-row">
            <div className="add-new-week" onClick={() => setAddingWeek(true)}>
              <div className="add-new-week-button">
                <SvgIcon src={"/img/new-icons/add.svg"} />
              </div>
              <p>{t("flexible.addNewWeek")}</p>
            </div>
            {onImport && (
              <div className="add-new-week" onClick={onImport}>
                <div className="add-new-week-button">
                  <SvgIcon src={"/img/new-icons/import.svg"} />
                </div>
                <p>{t("flexible.importTemplate.action")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {duplicating && (
        <WeekStartModal
          cycle={cycle}
          title={t("flexible.weekStart.duplicateTitle")}
          onClose={() => setDuplicating(null)}
          onConfirm={(startDate) => {
            const order = duplicating.order;
            setDuplicating(null);
            duplicateWeek(order, startDate);
          }}
        />
      )}

      {addingWeek && (
        <WeekStartModal
          cycle={cycle}
          title={t("flexible.weekStart.addTitle")}
          onClose={() => setAddingWeek(false)}
          onConfirm={(startDate) => {
            setAddingWeek(false);
            addWeek(startDate);
          }}
        />
      )}
    </div>
  );
};

/**
 * Program name and training split.
 *
 * The date range is meant to be READ, not calculated: it says "ongoing" while
 * nothing is queued behind the live week, and flips to a computed end date the
 * moment the coach schedules one.
 */
const FlexibleTopPanel = ({ cycle, athlete, liveRange, t }) => {
  const [name, setName] = useState(cycle?.name || "");
  const [dirty, setDirty] = useState(false);
  const inputRef = useRef(null);
  const splitType = resolveSplitType(cycle);

  // Name only now - the split moved to the flexible-split GET action below,
  // and repeat lives on each week row instead of here (only the live week's
  // flag ever governs, so a single program-level switch could no longer say
  // which week it meant).
  const saveSettings = (body) => {
    submit(`/actions/cycle/${cycle._id}/flexible-settings`, {
      method: "POST",
      body: { athlete, ...body },
    });
  };

  const saveName = () => {
    const trimmed = name.trim();
    if (!dirty || !trimmed) {
      setName(cycle?.name || "");
      setDirty(false);
      return;
    }
    setDirty(false);
    setName(trimmed);
    saveSettings({ name: trimmed });
  };

  return (
    <section className="flexible-top-panel">
      <span className="flexible-panel-range opacity-70">
        {liveRange
          ? liveRange.ongoing
            ? t("flexible.manager.rangeOngoing", {
                start: pretty(liveRange.startDate),
              })
            : t("flexible.manager.rangeUntil", {
                start: pretty(liveRange.startDate),
                end: pretty(liveRange.endDate),
              })
          : t("flexible.manager.notStarted")}
      </span>

      <div className="flexible-panel-fields">
        <div className="flexible-panel-field">
          <label htmlFor="flexible-program-name">
            {t("flexible.manager.programName")}
          </label>
          <div className="flexible-name-input">
            <input
              id="flexible-program-name"
              ref={inputRef}
              type="text"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setDirty(true);
              }}
              onFocus={() => setDirty(false)}
              onBlur={saveName}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  inputRef.current?.blur();
                } else if (event.key === "Escape") {
                  setName(cycle?.name || "");
                  setDirty(false);
                  inputRef.current?.blur();
                }
              }}
            />
            {dirty && (
              // biome-ignore lint/a11y/useSemanticElements: mousedown has to fire (and preventDefault) before the input's own blur, which a <button> can't do here
              <a
                role="button"
                href="#_"
                className="flexible-name-save"
                title={t("flexible.manager.save")}
                onMouseDown={(event) => {
                  event.preventDefault();
                  saveName();
                }}
              >
                <SvgIcon
                  src="/img/new-icons/chip-success.svg"
                  noFilterOnLight
                />
              </a>
            )}
          </div>
        </div>

        <div className="flexible-panel-field">
          <span className="flexible-panel-field-label">
            {t("flexible.manager.trainingType")}
          </span>
          {/* GET links (flexible-split action): res.refreshWith({}, {url:
              "back"}) redirects to the Referrer, which already carries
              ?athlete=... - nothing to attach here. */}
          <div
            className="flexible-split-options"
            role="radiogroup"
            aria-label={t("flexible.manager.trainingType")}
          >
            {[SPLIT_SUGGESTED, SPLIT_FORCED].map((mode) => {
              const active = splitType === mode;
              return (
                // biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up radio styling
                <a
                  key={mode}
                  role="radio"
                  aria-checked={active}
                  className="flexible-split-option"
                  href={`/actions/cycle/${cycle._id}/flexible-split/${mode}`}
                  onClick={(event) => {
                    if (active) event.preventDefault();
                  }}
                >
                  <div className="input-wrapper">
                    {/* disabled, not just readOnly: readOnly has no effect on
                        a radio input, so it would still toggle natively on
                        click if left interactive - this is a display only,
                        the <a> above is what navigates. */}
                    <input type="radio" checked={active} disabled readOnly />
                    <span className="radio-input">
                      <span className="radio" />
                    </span>
                  </div>
                  <span className="flexible-split-option-text">
                    <span className="s1">{t(`flexible.split.${mode}`)}</span>
                    <span className="p4 opacity-70">
                      {t(`flexible.splitDesc.${mode}`)}
                    </span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * One authored week: its window, its most recent occurrence's claims, and the
 * slots the coach programmed. `occurrence` is null for a week that has been
 * queued ahead but hasn't started yet - it renders with an empty ledger.
 */
const WeekRow = ({
  week,
  occurrence,
  cycle,
  athlete,
  isLive,
  isLastWeek,
  isHistory,
  weekExercises,
  weekSections,
  setExerciseWeek,
  loading,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  units,
  webAppTheme,
  onDuplicate,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const backgroundColor = webAppTheme === "dark" ? "#363C43" : "#F1F3F6";
  const foregroundColor = webAppTheme === "dark" ? "#40464D" : "#E0E4EA";

  const sessions = [...(week?.sessions || [])].sort(
    (a, b) => a.order - b.order
  );
  const splitType = resolveSplitType(cycle, week);
  const exercises = weekExercises[week.order];

  const claimByOrder = {};
  for (const claim of occurrence?.claims || [])
    claimByOrder[Number(claim.order)] = claim;

  // Only the LIVE week's own flag ever governs (an older week's is inert), so
  // that is the only row the border and toggle can visually mark as repeating.
  const repeats = week?.repeatActiveWeek !== false;
  const isRepeatingNow = isLive && repeats;

  // Same rule as Teams' own delete button (only the last week, never the
  // first), plus one flexible-specific guard: never a week that has already
  // started. Week 1 anchors the whole lattice every later week measures from,
  // and a started week's occurrence may already carry real claims - deleting
  // either would corrupt the program, not just this one row.
  const today = Number(moment().format("YYYYMMDD"));
  const canDelete =
    isLastWeek &&
    !isHistory &&
    Number(week.order) !== 1 &&
    Number(week.startDate) > today;

  // The week's full nominal span (its own start through the day before
  // whatever week took over, or "ongoing" while it's still the last one) -
  // NOT the most recent occurrence's own 7-day slice. Using the occurrence's
  // window here made this column regress to a single week whenever "today"
  // hadn't caught up to the week's later re-issues yet: a week superseded by
  // a future-queued one showed only its very first occurrence instead of its
  // whole run, and a lone repeating week showed this week's dates instead of
  // reading as open-ended.
  const range = authoredWeekRange(cycle, week.order);

  const toggleRepeat = (event) => {
    event.stopPropagation();
    submit(`/actions/cycle/${cycle._id}/flexible-week-repeat`, {
      method: "POST",
      body: { athlete, programOrder: week.order, repeatActiveWeek: !repeats },
    });
  };

  // One row per authored week now, so the card only needs to size
  // independently from every OTHER week's card - keying on the week is enough.
  const cardClass = `workout-${week.order}-card`;

  // Slot cards are flex columns whose height follows their contents, so an
  // empty D5 renders as a stub beside a populated D1. Level them to the
  // tallest once the exercises land, exactly as the team manager does.
  useEffect(() => {
    if (!isOpen || !exercises) return;

    const cards = Array.from(document.getElementsByClassName(cardClass));
    if (!cards.length) return;

    for (const card of cards) card.style.height = "auto";
    const tallest = Math.max(...cards.map((card) => card.clientHeight));
    for (const card of cards) card.style.height = `${tallest}px`;
  }, [isOpen, exercises, cardClass]);

  return (
    <div>
      <div
        className={`program-row ${isOpen ? "rounded-b-none!" : ""} ${isRepeatingNow ? "flexible-repeating-row" : ""}`}
        onClick={(event) => {
          if (event.target.closest("a") || event.target.closest("button")) {
            return;
          }
          setIsOpen(!isOpen);
          setExerciseWeek(week.order);
        }}
      >
        {/* Same six cells as the team week row, so the teams layout applies
            unchanged: the chevron lives inside the first cell, not beside it. */}
        <div>
          <SvgIcon
            src="/img/new-icons/arrow-square-right.svg"
            classNames={isOpen ? "turned" : ""}
          />
          <div className="flex flex-col gap-2">
            <p>
              {pretty(range?.startDate)} &ndash;{" "}
              {range?.endDate
                ? pretty(range.endDate)
                : t("flexible.manager.ongoing")}
            </p>
            {isLive && (
              <span className="active-week-indicator">
                {t("flexible.manager.activeWeek")}
              </span>
            )}
          </div>
        </div>
        <div>
          <p>{week.name || ""}</p>
        </div>
        <div>
          <p>
            {occurrence?.claimedCount || 0}/
            {occurrence?.total ?? sessions.length}
          </p>
        </div>
        <div className="flex flex-col! items-start! gap-2">
          {/* The chip already names the split ("Forced") - showing the plain
              label above it too was a literal duplicate (and, with the chip
              and its description sharing one cell, the label was getting
              clipped). Suggested has no chip, so it still needs the label. */}
          {splitType === SPLIT_FORCED ? (
            <span className="forced-split-indicator">
              <SvgIcon
                classNames="forced-split-indicator-icon"
                src="/img/new-icons/lock-closed.svg"
                useCurrentColor
              />
              {t("flexible.split.forced")}
            </span>
          ) : (
            <p>{t(`flexible.split.${splitType}`)}</p>
          )}
        </div>
        <div>
          <p>
            {occurrence?.daysTrained?.length
              ? occurrence.daysTrained.map(prettyShort).join(", ")
              : t("flexible.manager.noneTrained")}
          </p>
        </div>
        <div className="row-buttons z-10">
          <div className="flexible-row-actions">
            {/* Status, not an action - only the week actually governing right
                now (isRepeatingNow, not just this row's own flag) ever shows
                it, so there's never ambiguity about which row is repeating.
                "Until" comes from the same authoredWeekRange already computed
                above for the date column - a next authored week is exactly
                what ends the repeat, flag or no flag. Sits in the action
                cluster (not as a sibling that justify-between can shove to
                the other end of the cell). */}
            {isRepeatingNow && (
              <ToolTip
                id={`flexible-repeating-badge-tooltip-${week.order}`}
                body={t("flexible.manager.repeatingBadgeHint")}
                place="top"
                className="flexible-repeating-badge-tooltip"
              >
                <span className="flexible-repeating-badge">
                  {range?.endDate
                    ? t("flexible.manager.repeatingUntil", {
                        date: prettyMonthDay(range.endDate),
                      })
                    : t("flexible.manager.repeating")}
                </span>
              </ToolTip>
            )}
            {isLastWeek && !isHistory && (
              <ToolTip
                id={`flexible-duplicate-tooltip-${week.order}`}
                body={t("flexible.manager.duplicateWeek")}
              >
                <button
                  type="button"
                  data-button-type="secondary"
                  data-icon
                  data-icon-only
                  onClick={(event) => {
                    event.stopPropagation();
                    onDuplicate();
                  }}
                >
                  <SvgIcon
                    className="yellow"
                    src="/img/new-icons/duplicate.svg"
                  />
                </button>
              </ToolTip>
            )}
            {canDelete && (
              <a
                role="button"
                data-button-type="secondary"
                data-icon
                data-icon-only
                className="edit"
                href={`/actions/cycle/delete-program/${cycle._id}/${week.order}/?isFromCoachingPortal=false&isFromTeams=true`}
              >
                <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
              </a>
            )}
            <a
              role="button"
              data-button-type="secondary"
              href={`/athlete-portal/programs/edit-program/${cycle._id}?program=${week.order}&storeType=flexible${athlete ? `&athlete=${athlete}` : ""}${isHistory ? "&readOnly=true" : ""}`}
            >
              {isHistory
                ? t("flexible.manager.view")
                : t("flexible.manager.edit")}
            </a>
            {/* The action: this row's OWN flag, regardless of whether it's
                the live week - matches toggleRepeat/repeats, which have
                always applied per-row (only the effect is live-week-only).
                The LABEL text isn't this row's own flag alone though - "Repeating"
                unconditionally read as every toggled-on row already being live,
                which a merely-queued future week (or a stale flag on an already-
                superseded history row - isHistory, never actually live) is not.
                Live keeps the simple word; everything else names the setting
                instead of a status that isn't true yet (or isn't true anymore). */}
            <ToolTip
              id={`flexible-repeat-tooltip-${week.order}`}
              body={t("flexible.manager.repeatHint")}
              place="top"
            >
              {/* stopPropagation on the label too, not just toggleRepeat's
                  own onChange handler - a label's click bubbles independently
                  of its input's change event, and the row's own onClick only
                  ever excludes closest("a")/closest("button"). */}
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: the label's own <input type="checkbox"> is already fully keyboard-operable; this onClick only stops propagation to the row */}
              <label
                className="flexible-repeat-toggle"
                onClick={(event) => event.stopPropagation()}
              >
                <SvgIcon
                  src="/img/new-icons/vuesax-linear-refresh-2.svg"
                  useCurrentColor={repeats}
                  classNames={
                    repeats ? "flexible-repeat-on" : "flexible-repeat-icon"
                  }
                  alt={t("flexible.manager.repeat")}
                />
                <span>
                  {isLive
                    ? t("flexible.manager.repeating")
                    : !isHistory && repeats
                      ? t("flexible.manager.repeatToggleFuture")
                      : t("flexible.manager.repeatToggleOff")}
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={repeats}
                  checked={repeats}
                  onChange={toggleRepeat}
                />
              </label>
            </ToolTip>
          </div>
        </div>
      </div>

      {isOpen &&
        (exercises ? (
          <article className={`workout-${week.order}-list`}>
            {/* Always seven cards, D1..D7, so the row stays evenly laid out
                whatever the coach has filled in - the same shape the team
                manager gets from mapping the seven weekdays. Slots past
                `sessions` are simply empty. */}
            {SLOT_ORDERS.map((slotOrder) => {
              const session =
                sessions.find(
                  (candidate) => Number(candidate.order) === slotOrder
                ) || null;
              const claim = claimByOrder[slotOrder];
              const sessionSections = (weekSections?.[week.order] || []).filter(
                (section) =>
                  section.cycle &&
                  section.cycle.programWeek === 1 &&
                  section.cycle.programSession === slotOrder
              );

              // The client keeps whatever workout they were given when they
              // scheduled it; a later coach edit only reaches unclaimed slots.
              const editedAfterClaim =
                claim?.claimedAt &&
                week?.modifiedDate &&
                Number(week.modifiedDate) > Number(claim.claimedAt);

              return (
                <div
                  key={`${week.order}-slot-${slotOrder}`}
                  className={`exercise-card ${cardClass}${
                    session && splitType === SPLIT_FORCED
                      ? " forced-prescribed-day"
                      : ""
                  }`}
                >
                  <div className="flex flex-row items-center gap-2 ml-2 mt-1">
                    <p className="s1 ml-2 mt-1">
                      {t("flexible.manager.slotLabel", { index: slotOrder })}
                      {/* Both modes now carry a weekday: forced pins the slot
                          to it, suggested just shows it as guidance. */}
                      {session && (
                        <span className="p4 opacity-70 ml-2">
                          {moment()
                            .day(session.workoutDay || 0)
                            .format("dddd")}
                        </span>
                      )}
                    </p>
                    {session && splitType === SPLIT_FORCED && (
                      <ToolTip
                        id={`flexible-lock-${week.order}-${slotOrder}`}
                        body={t("flexible.manager.forcedHint")}
                      >
                        <SvgIcon
                          classNames="forced-split-lock"
                          src="/img/new-icons/lock-closed.svg"
                        />
                      </ToolTip>
                    )}
                  </div>

                  {/* {session && (
                    <p className="p4 ml-4 opacity-70">
                      {claim
                        ? t("flexible.manager.scheduledOn", {
                            date: prettyShort(claim.date),
                          })
                        : t("flexible.manager.notScheduled")}
                      {claim?.trained && (
                        <span className="ml-2">
                          {t("flexible.manager.trained")}
                        </span>
                      )}
                    </p>
                  )} */}

                  {/* {editedAfterClaim && (
                    <p className="p4 ml-4 flexible-edited-badge">
                      {t("flexible.manager.editedAfterClaim")}
                    </p>
                  )} */}

                  <SlotExerciseList
                    session={session ? exercises?.[slotOrder] : null}
                    sessionSections={sessionSections}
                    keyPrefix={`${week.order}-${slotOrder}`}
                    withWeight={withWeight}
                    fromMetric={fromMetric}
                    intensityMeasure={intensityMeasure}
                    isRiR={isRiR}
                    units={units}
                  />
                </div>
              );
            })}
          </article>
        ) : loading !== week.order ? (
          <article>
            <section className="no-data-state">
              <p className="s1">{t("flexible.manager.noDataToShow")}</p>
              <p className="p4 opacity-70 w-48 text-center">
                {t("flexible.manager.editHint")}
              </p>
            </section>
          </article>
        ) : (
          <article>
            <section className="loading">
              <CardsSkeleton
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
              />
            </section>
          </article>
        ))}
    </div>
  );
};

export default FlexibleProgramManager;
